import { useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import type { DevicePermissionStatus } from '@electron/shared/permission.types';

export interface DevicePermissionConnectResult {
  success: boolean;
  needsSystemSettings?: boolean;
}

export interface DevicePermissionBannerProps {
  status: DevicePermissionStatus | null;
  /** e.g. "Calendar", "Reminders", "Contacts" */
  domain: string;
  /** e.g. "calendars", "reminders", "contacts" */
  domainPlural?: string;
  /** What the granted and denied titles call the permission; defaults to "<domain> access" */
  accessName?: string;
  /** The OS permission prompt. Resolves like `handleEventKitConnect` / `handleContactsConnect`. Without one, Grant access opens System Settings. */
  onGrantAccess?: () => Promise<DevicePermissionConnectResult>;
  onOpenSettings: () => void | Promise<unknown>;
}

type BannerVariant = 'granted' | 'needsPrompt' | 'blocked';

function variantFor(status: DevicePermissionStatus): BannerVariant {
  if (status === 'authorized') return 'granted';
  if (status === 'not-determined') return 'needsPrompt';
  return 'blocked';
}

const CONTAINER_CLASSES: Record<BannerVariant, string> = {
  granted:
    'border-green-300/40 bg-green-200/40 dark:border-green-900/40 dark:bg-green-950/50',
  needsPrompt:
    'border-amber-300/40 bg-amber-200/40 dark:border-amber-900/40 dark:bg-amber-950/50',
  blocked:
    'border-red-300/40 bg-red-200/40 dark:border-red-900/40 dark:bg-red-950/50',
};

const ICON_CLASSES: Record<BannerVariant, string> = {
  granted: 'text-green-600 dark:text-green-400',
  needsPrompt: 'text-amber-600 dark:text-amber-400',
  blocked: 'text-red-600 dark:text-red-400',
};

const VARIANT_ICON: Record<BannerVariant, typeof Icons.check> = {
  granted: Icons.check,
  needsPrompt: Icons.warn,
  blocked: Icons.lock,
};

function titleFor(
  variant: BannerVariant,
  domain: string,
  accessName: string
): string {
  switch (variant) {
    case 'granted':
      return `${accessName} granted`;
    case 'needsPrompt':
      return `${domain} needs your permission`;
    case 'blocked':
      return `${accessName} denied`;
  }
}

/** Granted needs no explanation — the green single line is the message.
 * Only the two states asking the user to act carry a description. */
function bodyFor(
  variant: BannerVariant,
  domainPlural: string | undefined
): string | null {
  switch (variant) {
    case 'granted':
      return null;
    case 'needsPrompt':
      return domainPlural
        ? `Littlebird can't see your ${domainPlural} until you allow access.`
        : 'These tools stay off until you allow access.';
    case 'blocked':
      return 'macOS is blocking Littlebird. These tools stay off until you allow access in System Settings.';
  }
}

/**
 * Three-state permission banner for macOS on-device providers (EventKit,
 * Contacts). "Grant access" calls the OS prompt directly and falls back to
 * opening System Settings when the prompt itself can't ask again —
 * mirroring the `needsSystemSettings` check `useIntegrationActions` uses,
 * but opening Settings straight away instead of surfacing another modal.
 */
export function DevicePermissionBanner({
  status,
  domain,
  domainPlural,
  accessName = `${domain} access`,
  onGrantAccess,
  onOpenSettings,
}: DevicePermissionBannerProps) {
  const [pending, setPending] = useState(false);

  if (status === null) return null;

  const variant = variantFor(status);
  const Icon = VARIANT_ICON[variant];

  const handleGrantAccess = async () => {
    setPending(true);
    try {
      if (!onGrantAccess) {
        await onOpenSettings();
        return;
      }
      const result = await onGrantAccess();
      if (!result.success && result.needsSystemSettings) {
        await onOpenSettings();
      }
    } catch (error) {
      toast.error(`Couldn't request ${domain} access`);
      console.error('Device permission prompt failed:', error);
    } finally {
      setPending(false);
    }
  };

  return (
    <XStack
      className={cn(
        'items-center justify-between gap-4 rounded-lg border px-4',
        variant === 'granted' ? 'py-2' : 'py-3',
        CONTAINER_CLASSES[variant]
      )}
    >
      <XStack className="items-start gap-3">
        <Icon className={cn('mt-0.5 size-4 shrink-0', ICON_CLASSES[variant])} />
        <YStack className="gap-0.5">
          <p className="text-sm font-medium text-foreground">
            {titleFor(variant, domain, accessName)}
          </p>
          {bodyFor(variant, domainPlural) && (
            <p className="text-xs text-foreground-muted">
              {bodyFor(variant, domainPlural)}
            </p>
          )}
        </YStack>
      </XStack>
      {variant === 'needsPrompt' ? (
        <Button
          size="sm"
          disabled={pending}
          onClick={() => void handleGrantAccess()}
        >
          {pending ? 'Requesting…' : 'Grant access'}
        </Button>
      ) : variant === 'granted' ? (
        // Nothing to do in the granted state, so the escape hatch is a quiet
        // text link rather than a button competing for attention.
        <button
          type="button"
          onClick={() => void onOpenSettings()}
          className="inline-flex shrink-0 items-center gap-1 text-xs text-foreground-muted transition-colors hover:text-foreground"
        >
          Manage in System Settings
          <Icons.externalLink className="size-3" />
        </button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => void onOpenSettings()}
        >
          Open System Settings
          <Icons.externalLink className="size-3.5" />
        </Button>
      )}
    </XStack>
  );
}
